// ---------------------------------------------------------------------------
// Page side of the taskbar App Badge
//
// The page never paints the badge itself. It posts each server observation of
// the owed-work count to the service worker, which is the one writer. The
// worker fences writes by the server observation instant (see badgeWriter.ts),
// so a page repaint of an older read cannot overwrite a newer push, and the
// reverse.
//
// Why not paint from the page: a page write has no ordering against the
// worker's writes. The page's Badging calls are also the ones Windows coalesces
// while the app is running, so the repaint has to go through the worker.
// ---------------------------------------------------------------------------

import { BADGE_OBSERVED, type BadgeObservedMessage } from "../push/swMessages";
import { toObservationMillis } from "./badgeWriter";

export interface AppBadgeObservationInput {
    readonly count: number;
    /** Server read instant (ISO 8601), as returned on /workitems/mine. */
    readonly asOf: string;
}

/**
 * Hands one server observation of the owed-work count to the worker.
 *
 * Best-effort and never rejects: a badge is cosmetic, so an unsupported
 * surface, a worker that is not registered yet, or a malformed instant
 * degrades to "no update" rather than an application error.
 */
export async function publishAppBadgeObservation({
    count,
    asOf,
}: AppBadgeObservationInput): Promise<void> {
    const asOfMs = toObservationMillis(asOf);
    if (asOfMs === undefined || typeof navigator === "undefined") {
        return;
    }
    if (!("serviceWorker" in navigator)) {
        return;
    }
    try {
        const registration = await navigator.serviceWorker.ready;
        const target = registration.active;
        if (target === null) {
            return;
        }
        const message: BadgeObservedMessage = { type: BADGE_OBSERVED, count, asOfMs };
        target.postMessage(message);
    } catch {
        // Best-effort: a badge update never surfaces as an error.
    }
}
